import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { fmtMoney, fmtPct } from "@/lib/api";

const Row = ({ label, amount, first, testid, extra }) => (
  <div className="ledger-row" data-testid={testid}>
    <span className="pr-4">{label}{extra}</span>
    <span className="num text-right">{fmtMoney(amount, first)}</span>
  </div>
);

const Total = ({ label, amount, double, testid }) => (
  <div className={`ledger-row font-semibold text-slate-900 ${double ? "mt-3" : ""}`}>
    <span>{label}</span>
    <span className={`num text-right ${double ? "rule-double" : "rule-single"}`} data-testid={testid}>
      {fmtMoney(amount, true)}
    </span>
  </div>
);

const Section = ({ title, children }) => (
  <section className="mt-8">
    <h3 className="font-serif text-xl text-slate-900 border-b border-slate-300 pb-1 mb-2">{title}</h3>
    {children}
  </section>
);

const InternalMark = ({ item, base }) => (
  <>
    <Link to={`${base}/${item.entity_id}`} className="t-mark" title="Internal entity – valued from its own statement"
      data-testid={`internal-link-${item.entity_id}`}>t</Link>
    {item.missing_pct && (
      <span className="warn-badge ml-2" data-testid="missing-pct-badge">
        <AlertTriangle className="h-3 w-3" /> % missing
      </span>
    )}
    {!item.missing_pct && <span className="ml-2 text-xs text-slate-400 font-mono">{fmtPct(item.pct)}</span>}
  </>
);

export const Statement = ({ rep, title, linkBase = "/reports/entities" }) => (
  <article className="statement" data-testid="statement">
    <div className="flex items-start justify-between gap-6 border-b-2 border-slate-900 pb-4">
      <div className="min-w-0">
        <div className="small-label">{title}</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-slate-900 tracking-tight mt-2 break-words" data-testid="statement-entity-name">
          {rep.entity.name}
        </h2>
      </div>
      <div className="text-right font-mono text-xs text-slate-500 space-y-1 shrink-0">
        <div>CODE <span className="text-slate-900" data-testid="statement-code">{rep.entity.code || "—"}</span></div>
        <div>EID <span className="text-slate-900">{rep.entity.eid ?? "—"}</span></div>
        <div>AS OF <span className="text-slate-900" data-testid="statement-period">{rep.period}</span></div>
      </div>
    </div>
    {!rep.has_data && (
      <p className="mt-4 text-sm text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2" data-testid="statement-no-data">
        No uploaded balances for this month end — values come only from holdings in other entities.
      </p>
    )}
    <Section title="Assets">
      {rep.assets.map((a, k) => (
        <Row key={a.category} label={a.category} amount={a.amount} first={k === 0} testid={`asset-row-${k}`} />
      ))}
      <Total label="Total Assets" amount={rep.total_assets} testid="total-assets" />
    </Section>
    <Section title="Liabilities">
      {rep.liabilities.map((a, k) => (
        <Row key={a.category} label={a.category} amount={a.amount} first={k === 0} testid={`liability-row-${k}`} />
      ))}
      <Total label="Total Liabilities" amount={rep.total_liabilities} testid="total-liabilities" />
    </Section>
    <Total label="Net Assets" amount={rep.net_assets} double testid="net-assets" />
    {rep.owners.length > 0 && (
      <Section title="Owners">
        {rep.owners.map((o, k) => (
          <Row key={o.entity_id} first={k === 0} amount={o.amount} testid={`owner-row-${k}`}
            label={<><span className="num inline-block w-20 text-slate-500">{fmtPct(o.pct)}</span>{o.name}</>} />
        ))}
        <Total label="Total Owners" amount={rep.owners_total} testid="total-owners" />
        {Math.abs(rep.owners_total_pct - 100) > 0.05 && (
          <p className="warn-badge mt-3" data-testid="owners-pct-warning">
            <AlertTriangle className="h-3 w-3" /> Owners total {fmtPct(rep.owners_total_pct)}
          </p>
        )}
      </Section>
    )}
    {rep.investments.length > 0 && (
      <Section title="Detail of Investments in Partnerships, Private Corporations and LLCs">
        {rep.investments.map((d, k) => (
          <Row key={`${d.label}-${k}`} first={k === 0} amount={d.amount} testid={`investment-row-${k}`} label={d.label}
            extra={d.internal ? <InternalMark item={d} base={linkBase} /> : null} />
        ))}
        <Total label="Total Detail of Investments" amount={rep.investments_total} testid="total-investments" />
        <p className="text-xs italic text-slate-500 mt-3">
          <span className="t-mark mr-1">t</span> internal entity, valued at this holder's percentage of that entity's net assets.
        </p>
      </Section>
    )}
  </article>
);
